import { IsPackageComponentContext } from 'context/IsPackageComponentContext';
import { useCallback, useContext } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { loadNewPage } from 'store/slices/pageListSlice';
import { DEPEND_CONDITION_RULES, isPageHidden } from '../services/DependencyService';
import { isPackageStepByPageId } from '../services/PackageService';
import { getActivePage } from '../services/PageService';
import { useFormSubmit } from './useFormSubmit';
import { usePageParam } from './usePageParam';

export const useNavigation = () => {
	const dispatch = useAppDispatch();
	const { orderedPageList } = useAppSelector((state) => state.pages);
	const { setPageParam } = usePageParam();

	const { resetFormField } = useFormSubmit();

	const isStepHidden = (pageId: number, forward: boolean) => {
		const activePage = getActivePage(pageId);
		const isHidden = isPageHidden(activePage);
		if (forward && isHidden) {
			if (activePage?.contentDependency?.dependsOn?.uid) {
				if (activePage?.contentDependency?.dependCondition === DEPEND_CONDITION_RULES.EMPTY) {
					//don't reset formFields
					return true;
				}
			}

			// reset formField values on hidden pages when navigation forward
			activePage.fields?.items?.forEach((field) => {
				const formFieldId = field?.content?.uniqueIdentifier?.uid || field?.content?.name;
				// fields will still have value on the react-hook-form but not in the store
				if (field?.content?.contentDependency?.dependsOn?.uid) {
					if (field?.content?.contentDependency?.dependCondition !== DEPEND_CONDITION_RULES.EMPTY) {
						return;
					}
				}
				if (formFieldId) {
					resetFormField(formFieldId);
				}
			});
		}
		return isHidden;
	};

	const { setIsVisible } = useContext(IsPackageComponentContext);
	const navigate = useCallback(
		(pageId: number, forward: boolean) => {
			const currentInx = orderedPageList.indexOf(pageId);
			let pageList = [...orderedPageList];

			if (forward) {
				pageList = pageList.splice(currentInx + 1);
			} else {
				pageList = pageList.splice(0, currentInx);
				pageList.reverse();
			}

			const navigateTo = pageList.find((id) => !isStepHidden(id, forward));

			if (navigateTo) {
				setTimeout(() => {
					setIsVisible(isPackageStepByPageId(navigateTo));
				}, 100);
			}

			navigateTo ? setPageParam(navigateTo.toString()) : dispatch(loadNewPage());
		},
		[dispatch, isStepHidden, orderedPageList, setIsVisible, setPageParam]
	);

	return { navigate };
};
